import type {
  ReportHistory,
  ReportHistoryResults,
  ReportKind,
  ReportLaunchResponse,
  ReportMetadata,
  ReportPatch,
} from '../../app/modules/types/report'
import { apiClient } from '../client'

export type ReportFileType = 'pdf' | 'html'
export type ReportLang = 'en' | 'fa'
export type PeriodPreset = '7d' | '30d' | '90d'

export type ExecutivePosturePayload = ReportMetadata & {
  period?: { preset: PeriodPreset } | { from: string; to: string }
  type: ReportFileType
  lang: ReportLang
}

export type AssetDeepDivePayload = ReportMetadata & {
  asset_type: 'host' | 'ipaddress'
  asset_id: string
  type: ReportFileType
  lang: ReportLang
}

/**
 * Reports API endpoints
 */
export const reportsApi = {
  /**
   * Get reports list
   */
  getList: async (params?: string): Promise<ReportHistoryResults> => {
    const url = params ? `/reports/?${params}` : '/reports/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Get report by ID
   */
  getById: async (reportId: string): Promise<ReportHistory> => {
    const { data } = await apiClient.get(`/reports/${reportId}/`)
    return data
  },

  /**
   * Get report history
   */
  getHistory: async (params?: string): Promise<any> => {
    const url = params ? `/reports/?${params}` : '/reports/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Create report
   */
  create: async (payload: any): Promise<any> => {
    const { data } = await apiClient.post('/reports/', payload)
    return data
  },

  /**
   * Download report
   */
  download: async (reportId: string): Promise<{
    expires_in_minutes: number
    filename: string
    download_url: string
  }> => {
    const { data } = await apiClient.get(`/reports/${reportId}/download/`, {
      params: { format: 'json' },
    })
    return data
  },

  /**
   * Edit name / description / tags. Any subset; an empty `name` resets to the
   * generated default. Needs `report: edit`. Returns the full report.
   */
  update: async (reportId: string, patch: ReportPatch): Promise<ReportHistory> => {
    const { data } = await apiClient.patch(`/reports/${reportId}/`, patch)
    return data
  },

  /** Pin for the current user (idempotent). */
  pin: async (reportId: string): Promise<void> => {
    await apiClient.post(`/reports/${reportId}/pin/`)
  },

  /** Unpin for the current user (idempotent). */
  unpin: async (reportId: string): Promise<void> => {
    await apiClient.delete(`/reports/${reportId}/pin/`)
  },

  /** Distinct tags of the organization starting with `search`; a bare array, max 50. */
  getTags: async (search?: string): Promise<string[]> => {
    const { data } = await apiClient.get('/reports/tags/', {
      params: search ? { search } : undefined,
    })
    return data
  },

  /**
   * The name a launch with this body would get — the create form's placeholder.
   * Same body as the kind's launch endpoint; `type`/`lang` are optional here.
   */
  getDefaultName: async (kind: ReportKind, body: Record<string, unknown>): Promise<string> => {
    const { data } = await apiClient.post('/reports/default-name/', { kind, ...body })
    return data.default_name
  },

  /**
   * Delete report. 409 while it is still generating.
   */
  delete: async (reportId: string): Promise<void> => {
    await apiClient.delete(`/reports/${reportId}/`)
  },

  /**
   * Get scan reports table (scans with vulnerability filters)
   */
  getScanReportsTable: async (params?: string): Promise<any> => {
    const url = params ? `/scan/vulns-filter/?${params}` : '/scan/vulns-filter/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Get report templates
   */
  getTemplates: async (params?: string): Promise<any[]> => {
    const url = params ? `/report-templates/?${params}` : '/report-templates/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Get report template by ID
   */
  getTemplateById: async (templateId: string): Promise<any> => {
    const { data } = await apiClient.get(`/report-templates/${templateId}/`)
    return data
  },

  /**
   * Create report (single scan or multi-scan)
   */
  createReport: async (data: any, isSingleScan: boolean): Promise<ReportLaunchResponse> => {
    const endpoint = isSingleScan ? 'scan-report' : 'multi-scan-report'
    const response = await apiClient.post(`/reports/${endpoint}/`, data)
    return response.data
  },

  /**
   * Create executive posture report (org-wide, period-based)
   */
  createExecutivePostureReport: async (
    payload: ExecutivePosturePayload
  ): Promise<ReportLaunchResponse> => {
    const { data } = await apiClient.post('/reports/executive-posture-report/', payload)
    return data
  },

  /**
   * Create asset deep-dive report (single host or IP)
   */
  createAssetDeepDiveReport: async (
    payload: AssetDeepDivePayload
  ): Promise<ReportLaunchResponse> => {
    const { data } = await apiClient.post('/reports/asset-deep-dive-report/', payload)
    return data
  },

  /**
   * Create report template
   */
  createTemplate: async (data: FormData | Record<string, any>): Promise<any> => {
    const response = await apiClient.post('/report-templates/', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    })
    return response.data
  },

  /**
   * Update report template
   */
  updateTemplate: async (templateId: string, data: FormData | Record<string, any>): Promise<any> => {
    const response = await apiClient.put(`/report-templates/${templateId}/`, data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    })
    return response.data
  },

  /**
   * Delete report template
   */
  deleteTemplate: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/report-templates/${templateId}/`)
  },
}
